export const frankfurtArticle = {
  eyebrow: 'Event Report',
  title: 'Frankfurt Book Fair 2026',
  subtitle: 'Five days, one booth, and a wall of books',
  lead: 'Leavitt Peak Press returned from the world\'s largest publishing marketplace with a booth that did most of the talking — two walls of titles, a steady stream of readers, and five days of conversations that started with a cover and ended with a contract conversation.',
  excerpt: 'From October 7 to 11, our team held down Halle 3.1, stand A104 — two walls of title posters, a table of flyers and bookmarks, and a nonstop flow of readers, scouts, and rights buyers. Here is how the fair actually went.',
  date: 'October 7-11, 2026',
  published: 'October 19, 2026',
  location: 'Messe Frankfurt, Frankfurt, Germany',
  booth: 'Halle 3.1 / A104',
  readingTime: '6 min read',
  heroImage: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/04aa1d51-cd8a-4039-a544-58be26325257_compressed_IMG_2647.webp',
  heroImageAlt: 'Leavitt Peak Press booth at Frankfurt Book Fair 2026 with the United States of America sign and a wall of title posters',
  sideImage: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/476b629e-94fc-477d-a9b9-d8c911c4c7ae_compressed_IMG_2643.webp',
  sideImageAlt: 'Frankfurt Book Fair 2026 banner beside the Leavitt Peak Press poster wall',
  pullQuote: 'People did not walk past the booth. They stopped, pointed at a cover, and asked what it was about. That is the whole job of a book fair, and it happened all five days.',
};

export const frankfurtStats = [
  { value: '5', label: 'Days on the floor' },
  { value: '18', label: 'Genres on display' },
  { value: '1,500+', label: 'Titles in catalogue' },
  { value: '220', label: 'Authors represented' },
  { value: '6', label: 'Continents reached' },
];

export const frankfurtSections = [
  {
    heading: 'Five days inside Halle 3.1',
    paragraphs: [
      'The doors of Messe Frankfurt opened on the morning of October 7, and by the time the aisles filled, Leavitt Peak Press was already set: a corner stand in Halle 3.1, A104, a blue banner squared to the walkway, and a desk arranged the way it would stay for the next five days — laptop open, flyers fanned out, a glass dish of small giveaways, and a single copy of each featured title within arm\'s reach.',
      'Frankfurt is not a retail fair. Nobody comes to browse a shelf on a Saturday afternoon. It is the annual meeting point for the entire trade — publishers, literary agents, scouts, translators, distributors, librarians, and rights buyers from across the world — and every conversation at a stand like ours has the potential to turn into a translation, a territory, or a print run. That is exactly why the booth was built to be read from the aisle.',
    ],
  },
  {
    heading: 'A wall that did the talking',
    paragraphs: [
      'Instead of stacking books spine-out on a table, we papered two walls with individual title posters. Each one carried its own cover art, author name, genre, ISBN, format and price, plus a QR code that opened that book\'s page directly on a phone. Underneath, a strip of the same design carried the line that became the theme of the week: ideas, stories, connections.',
      'The result was a booth you could browse without touching anything. Visitors walked the wall the way they would walk a bookshop display, stopped where a cover caught them, scanned, and then stepped over to the desk with a question already formed. Our job stopped being "let me tell you about our catalogue" and became "let me tell you about that one." On the busiest afternoons there were three separate conversations going at once, all of them about different books.',
    ],
  },
  {
    heading: 'The titles readers kept circling back to',
    paragraphs: [
      'A few covers did the heavy lifting. Ruby Ransom drew the most second looks of the week — the artwork pulled people in from two aisles away, and the flyers we printed for it ran down to the last handful by Saturday. Brody Thompson: Texas Ranger held its own with the western crowd, and its table display, flyers and bookmarks became a small landmark for anyone looking for the American list.',
      'Captivity and The Himalayan Affair kept a steadier, more serious stream of visitors — readers who wanted to talk about the subject before they talked about the book. Around them the wall ran the full width of what we publish: The Alchemist, Siren Song, The House of the Rooster, Adaline\'s Time, Dress Your Cat, A Bar of Thieves, Syrup of Sirius, and a long tail of titles that each found their own two or three people across the five days. That range turned out to be the point: eighteen genres in one stand meant almost everyone who stopped found something they could reach for.',
    ],
  },
  {
    heading: 'Conversations, not just copies',
    paragraphs: [
      'Most of what happened at A104 never turned into a sale on the floor, and that was never the goal. Rights conversations ran throughout the week — publishers weighing translated editions, agents asking about submission windows, distributors working out which titles travel well in their markets, and librarians building out acquisition lists for the year ahead.',
      'The QR codes on the wall made those conversations faster than they have ever been. A visitor could scan a poster, read the blurb, check the format, and hand the phone back with a specific title in mind instead of a vague request for "the catalogue." Several follow-up calls were scheduled before the visitor left the stand, which is the real scoreboard at a fair like this.',
    ],
  },
  {
    heading: 'What the numbers behind the booth said',
    paragraphs: [
      'The stand itself was small. What sat behind it was not. The wall pointed to a catalogue spanning eighteen genres, more than fifteen hundred titles, and roughly two hundred and twenty authors — work reaching readers on six continents, in print, ebook, and audio.',
      'That scale is why a booth like A104 works. A visitor who came for one thriller could be handed a memoir, a children\'s title, or a military history without leaving the stand, and the QR codes meant none of those recommendations got lost on the way home.',
    ],
  },
  {
    heading: 'Thank you, Frankfurt',
    paragraphs: [
      'To everyone who stopped by Halle 3.1, A104 — the readers who scanned a code, the booksellers who took a flyer, the scouts who asked the hard questions, and the fellow publishers who shared a table with us — thank you. Frankfurt 2026 was our strongest showing at this fair yet, and it was built one conversation at a time.',
      'Rights enquiries from the fair are being handled now. If we met in Frankfurt and you are waiting on a follow-up, or if you missed us and want to talk about a title, the fastest route is our contact page. Photographs from the week are collected below.',
    ],
  },
];

export const frankfurtTitles = [
  {
    title: 'Ruby Ransom',
    author: 'L. Wehr',
    note: 'The cover that stopped traffic, two aisles out.',
  },
  {
    title: 'Brody Thompson: Texas Ranger',
    author: 'Stephen Perez',
    note: 'Our most requested western of the week.',
  },
  {
    title: 'Captivity',
    author: 'Helga McNeil',
    note: 'Serious readers, longer conversations.',
  },
  {
    title: 'The Himalayan Affair',
    author: '',
    note: 'Steady interest from European buyers.',
  },
  {
    title: 'The Alchemist',
    author: '',
    note: 'Fantasy that travelled across languages.',
  },
  {
    title: 'Siren Song',
    author: '',
    note: 'A quiet favourite on the poster wall.',
  },
];

export const frankfurtPhotos = [
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/04aa1d51-cd8a-4039-a544-58be26325257_compressed_IMG_2647.webp',
    caption: 'The full stand in Halle 3.1 — banner, poster wall and the reading table.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/476b629e-94fc-477d-a9b9-d8c911c4c7ae_compressed_IMG_2643.webp',
    caption: 'Frankfurt Book Fair 2026 banner squared to the aisle at A104.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/8003cc23-942f-4299-95e1-013700c80458_compressed_IMG_2644.webp',
    caption: 'Poster wall on the left, Ruby Ransom flyers on the desk.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/afa59125-e5e3-4903-a6e4-82fb6d3f2e85_compressed_IMG_2645.webp',
    caption: 'Twenty-plus title posters, each with its own scan code.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/56a13a98-fc07-43df-bcf6-5fe4faa72a1e_compressed_IMG_2646.webp',
    caption: 'The desk between visitors — laptop open, catalogues ready.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/6f0bd5ec-ede9-472a-8418-4fe66c2f9d32_compressed_IMG_2648.webp',
    caption: 'Brody Thompson: Texas Ranger took over the table midweek.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/1eb3ad04-17c7-443a-a341-4a6ac35cb61c_compressed_IMG_2649.webp',
    caption: 'Flyers, bookmarks and the giveaway dish that emptied daily.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/19efa519-0535-4d32-a6e0-93c80e02f452_compressed_IMG_2650.webp',
    caption: 'Every title printed with ISBN, format and price for buyers.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/79e6d0ac-a353-411a-a10b-2832309537de_compressed_IMG_2651.webp',
    caption: 'Late in the day, when the aisles finally thinned out.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/fabeb9cd-9837-42cf-a769-ba958519bc36_compressed_IMG_2652.webp',
    caption: 'Poster wall, banner and desk — the booth as it ran all week.',
  },
];

export const frankfurtAtAGlance = [
  { label: 'Fair', value: 'Frankfurt Book Fair 2026' },
  { label: 'Dates', value: 'October 7-11, 2026' },
  { label: 'Venue', value: 'Messe Frankfurt, Germany' },
  { label: 'Stand', value: 'Halle 3.1 / A104' },
  { label: 'Genres shown', value: '18' },
  { label: 'Team', value: 'Acquisitions, rights & editorial' },
];

export const frankfurtCollageIntro = {
  eyebrow: 'Close-Up on A104',
  heading: 'The corner that stopped the aisle',
  description:
    'Sixteen frames from behind the table — the poster wall, the shelf sign, the candy dish, the A Paws in Time bookmarks, and the flyers we kept restocking all week.',
};

export const frankfurtDetailPhotos = [
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/3e746970-0fb9-4c6a-a8ff-dd1bb57bbcdf_compressed_IMG_2653.webp',
    tag: 'The table',
    caption: 'Looking down on the desk: laptop open, crystal candy dish, and a fan of Brody Thompson flyers.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/7da2f26c-3870-49f5-9342-67492cdbad78_compressed_IMG_2682.webp',
    tag: 'The bookmark',
    caption: 'An A Paws in Time bookmark held up against the banner — one of the pieces readers kept picking up off the desk.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/e4890898-71c1-4f47-9d79-6bdb80028a4c_compressed_IMG_2655.webp',
    tag: 'The giveaway',
    caption: 'Stickers, pins and sweets in the glass dish that emptied every single day.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/3e51dbe7-0601-496b-999b-309338213d70_compressed_IMG_2683.webp',
    tag: 'The bookmark',
    caption: 'The same bookmark stepped back: A Paws in Time by Adrianne Hemenway, with the poster wall filling the left side.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/901b524d-f4e8-422b-81d4-b2e6229ed23e_compressed_IMG_2657.webp',
    tag: 'The wall',
    caption: 'Where the two walls meet — Captivity, The Alchemist and Ruby Ransom in one frame.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/b3e780c3-e608-4e2c-8b44-8ec8d6f23ef0_compressed_IMG_2688.webp',
    tag: 'The banner',
    caption: 'The roll-up banner reading Halle 3.1 / A104, right beside the open laptop and the day\'s flyers.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/d9f8e4fa-900a-41e9-9c04-64735718778e_compressed_IMG_2658.webp',
    tag: 'The sign',
    caption: 'Straight on: "Leavitt Peak Press, United States of America" above the reading table.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/865b403f-302d-4eab-853d-2fd27e08a340_compressed_IMG_2659.webp',
    tag: 'The wall',
    caption: 'Siren Song, Adaline\'s Time and Dress Your Cat holding the upper corner of the display.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/be931cde-6b4e-4e40-8cfd-1a77d8a121cf_compressed_IMG_2660.webp',
    tag: 'The angle',
    caption: 'Shot at a slight angle — the way most visitors met the stand for the first time.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/62e61ca7-6d25-4abd-8fe9-e0e4f7341816_compressed_IMG_2661.webp',
    tag: 'The corner',
    caption: 'Poster wall on the left, banner on the right, flyers squared away in between.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/a07c3190-5d8d-415e-9e4e-9550ef2a9be5_compressed_IMG_2684.webp',
    tag: 'The table',
    caption: 'The whole display in one frame — both poster walls, the shelf sign, the open laptop and the A Paws in Time stacks.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/a08174f6-e623-4f5d-ae53-b6e3c85ed194_compressed_IMG_2656.webp',
    tag: 'The stand',
    caption: 'The whole corner at once: both poster walls, the shelf sign and the roll-up banner.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/35ae75c7-1b20-456f-8dac-1443d5151391_compressed_IMG_2662.webp',
    tag: 'The stand',
    caption: 'The booth as it stood for all five days, from the aisle side of the table.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/0645bad2-3831-4b30-8eec-da7b2611dad6_compressed_IMG_2690.webp',
    tag: 'The corner',
    caption: 'The A104 corner from the aisle side, with the shelf sign reading Leavitt Peak Press, United States of America.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/b827f26c-feba-466b-84a7-ef5bf69168b5_compressed_IMG_2685.webp',
    tag: 'The table',
    caption: 'Tabletop view: flyers laid flat, bookmarks fanned out, and the crystal dish waiting for the next visitor.',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/a98e0736-4099-41b6-9475-372b91630012_compressed_IMG_2654.webp',
    tag: 'The banner',
    caption: 'The same corner, tighter — Frankfurt Book Fair 2026 printed across the banner at eye level.',
  },
];